/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MutableRef, Ref, useRef } from 'preact/hooks';
import { handleInput } from './events';
import { CommitDetail, InputDetail } from '../../utils/UNSAFE_meterTypes';

type getValueFromEvent = (event: PointerEvent) => number | undefined;

export function usePointerEvents(
  value: number | undefined,
  getValue: getValueFromEvent,
  targetRef: Ref<HTMLDivElement>,
  currentInputRef: MutableRef<number | undefined>,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) {
  const isPointerDown = useRef<boolean>(false);

  const pointerUpHandler = (event: PointerEvent) => {
    isPointerDown.current = false;
    const val = getValue(event);
    if (val !== undefined) {
      onCommit?.({ value: val } as CommitDetail);
    }
  };

  const hoverHandler = (event: PointerEvent) => {
    if (isPointerDown.current) {
      const val = getValue(event);
      if (val !== undefined && val != currentInputRef.current) {
        handleInput(val, currentInputRef, onInput);
      }
    }
  };

  const pointerDownHandler = (event: PointerEvent) => {
    isPointerDown.current = true;
    targetRef.current?.setPointerCapture(event.pointerId);
    const val = getValue(event);
    if (val !== undefined && val != currentInputRef.current) {
      handleInput(val, currentInputRef, onInput);
    }
  };

  const blurHandler = () => {
    isPointerDown.current = false;
    onCommit?.({ value: value } as CommitDetail);
  };

  return {
    onPointerUp: pointerUpHandler,
    onBlur: blurHandler,
    onPointerMove: hoverHandler,
    onPointerDown: pointerDownHandler
  };
}
